import { useMemo, useState } from "react";
import Button from "../../../../../shared/components/Button";
// import Dropdown from "../../../components/Dropdown";
import type { IDepositHistory } from "../interfaces/deposit.interface";
import MonthPicker from "../../../../../shared/components/MonthPicker";
import { useDepositDrawerRoute } from "../hooks/useDepositDrawerRoute";
import {
  buildDepositHistory,
  // DEPOSIT_TRANSACTION_TYPES,
  // getTransactionStatusLabel,
  // getTransactionTypeLabel,
  type TransactionHistoryFilters,
  useCryptoDepositHistory,
  useFiatDepositHistory,
  mapFiatDepositHistoryToTransactions,
  type TransactionHistoryFilterOptions,
} from "../../../services/transactions";

export default function Deposit() {
  const { showFundsDrawer } = useDepositDrawerRoute();
  const [queryFilters, setQueryFilters] = useState<TransactionHistoryFilters>(
    () => {
      const now = new Date();
      return {
        month: now.getMonth() + 1,
        year: now.getFullYear(),
      };
    }
  );
  // const [typeFilter, setTypeFilter] = useState<string>("all");
  // const [statusFilter, setStatusFilter] = useState<string>("all");

  const {
    data: cryptoTransactions,
    isLoading: isLoadingCrypto,
    isError: isCryptoError,
    // refetch: refetchCrypto,
    isFetching: isFetchingCrypto,
  } = useCryptoDepositHistory(queryFilters);

  const {
    data: fiatDepositHistory,
    isLoading: isLoadingFiat,
    isError: isFiatError,
    // refetch: refetchFiat,
    isFetching: isFetchingFiat,
  } = useFiatDepositHistory(queryFilters);

  const fiatTransactions = useMemo(
    () => mapFiatDepositHistoryToTransactions(fiatDepositHistory),
    [fiatDepositHistory]
  );

  const combinedTransactions = useMemo(
    () => [...(cryptoTransactions ?? []), ...(fiatTransactions ?? [])],
    [cryptoTransactions, fiatTransactions]
  );

  const isError = isCryptoError || isFiatError;
  const isLoading = isLoadingCrypto || isLoadingFiat;
  const isFetching = isFetchingCrypto || isFetchingFiat;

  // const refetchAll = () => Promise.all([refetchCrypto(), refetchFiat()]);

  // const typeOptions = useMemo(
  //   () => [
  //     { value: "all", label: "All Types" },
  //     ...DEPOSIT_TRANSACTION_TYPES.map((type) => ({
  //       value: type,
  //       label: getTransactionTypeLabel(type),
  //     })),
  //   ],
  //   []
  // );

  // const depositTypeSet = useMemo(
  //   () => new Set<string>(DEPOSIT_TRANSACTION_TYPES),
  //   []
  // );

  // const statusOptions = useMemo(() => {
  //   const availableStatuses = new Map<string, string>();

  //   (combinedTransactions ?? []).forEach((transaction) => {
  //     if (!depositTypeSet.has(transaction.type)) {
  //       return;
  //     }

  //     if (!availableStatuses.has(transaction.status)) {
  //       availableStatuses.set(
  //         transaction.status,
  //         getTransactionStatusLabel(transaction.status)
  //       );
  //     }
  //   });

  //   return [
  //     { value: "all", label: "All Statuses" },
  //     ...Array.from(availableStatuses.entries()).map(([value, label]) => ({
  //       value,
  //       label,
  //     })),
  //   ];
  // }, [combinedTransactions, depositTypeSet]);

  const filterOptions = useMemo<TransactionHistoryFilterOptions | undefined>(
    () => undefined,
    []
  );

  const depositHistory = useMemo<IDepositHistory[]>(
    () => buildDepositHistory(combinedTransactions, filterOptions),
    [combinedTransactions, filterOptions]
  );

  // const handleTypeFilterChange = (value: string) => {
  //   if (value === typeFilter) {
  //     return;
  //   }

  //   setTypeFilter(value);
  //   void refetchAll();
  // };

  // const handleStatusFilterChange = (value: string) => {
  //   if (value === statusFilter) {
  //     return;
  //   }

  //   setStatusFilter(value);
  //   void refetchAll();
  // };

  const handleMonthChange = (value: TransactionHistoryFilters) => {
    setQueryFilters(value);
  };

  const formatTransactionId = (id: string) => {
    if (!id) return "—";
    if (id.length <= 12) return id;
    return `${id.slice(0, 6)}...${id.slice(-4)}`;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold text-white">
          Your Deposit History
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button className="px-4!" variant="primary" onClick={showFundsDrawer}>
            Deposit Funds
          </Button>
          <MonthPicker value={queryFilters} onChange={handleMonthChange} />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-y-2">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-white/60">
              <th className="px-4 py-2">Date &amp; time</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Currency</th>
              <th className="px-4 py-2">Wallet / Bank Details</th>
              <th className="px-4 py-2">Transaction ID</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="space-y-2">
            {isError ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-sm text-red-400">
                  Unable to load your recent deposits right now. Please try
                  again.
                </td>
              </tr>
            ) : isLoading || (isFetching && !isLoading) ? (
              Array.from({ length: 4 }).map((_, index) => (
                <tr
                  key={`deposit-skeleton-${index}`}
                  className="bg-[#1F242A] rounded-2xl! border border-white/10 text-sm">
                  <td className="px-4 py-3">
                    <div className="h-4 w-28 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-20 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-12 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-32 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-24 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-6 w-16 bg-white/10 rounded-full" />
                  </td>
                </tr>
              ))
            ) : depositHistory.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-sm text-white/60">
                  No deposits yet.
                </td>
              </tr>
            ) : (
              depositHistory.map((item) => (
                <tr
                  key={item.id}
                  className="bg-[#1F242A] rounded-2xl! border border-white/10 text-sm text-white/80">
                  <td className="px-4 py-3 rounded-l-2xl">{item.date}</td>
                  <td className="px-4 py-3">{item.type}</td>
                  <td className="px-4 py-3">{item.currency}</td>
                  <td className="px-4 py-3">{item.walletDetail}</td>
                  <td className="px-4 py-3">
                    <span title={item.transactionId}>
                      {formatTransactionId(item.transactionId)}
                    </span>
                  </td>
                  <td className="px-4 py-3 rounded-r-2xl">
                    <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] text-white">
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
